/**
 * The table-geometry codec: the column widths Niobe stores in a GFM table's DELIMITER ROW.
 *
 *     | Item | Description | Cost |
 *     | -------------------- | ------------------------------------------------------------ | -------------------- |
 *
 * Each delimiter cell's DASH COUNT is that column's share of the table width, in whole percent
 * (here 20 / 60 / 20).
 *
 * WHY THE DELIMITER ROW. A part's CRDT is a markdown Y.Text and nothing else, and both markdown
 * stacks (the Rich editor and the server canonicaliser, `backend/adapters/markdown/canonical.ts`)
 * re-serialise it, so any geometry markdown cannot express is erased on the next reload, remote
 * edit or canonicalisation pass. The dash count is free: every markdown viewer accepts any run of
 * dashes, so a sized table is still plain, valid GFM. Same rule as the image `=NN%` title token
 * (`lib/image-geometry.ts`): geometry lives in the markdown.
 *
 * WHY A PERCENTAGE. A pixel number is meaningless once the document is rendered onto A4 by an
 * export, or read on another screen. Only PROPORTIONS are stored; a sized table always spans the
 * full text column.
 *
 * THE GRAMMAR. A delimiter row is EXPLICIT when it has at least two cells, every cell has at
 * least `COL_MIN_PCT` dashes, and the dash counts sum to exactly 100. Alignment colons are ignored
 * for the count. ANYTHING ELSE is auto (equal columns, today's behaviour) and is written back as
 * `---`. That includes the `---` form every existing part already holds — so those stay
 * byte-identical — and the arbitrary dash runs agents and LLMs write (`|------|-----------|`), so a
 * random agent table never suddenly gets odd widths.
 *
 * DEGRADE, NEVER THROW. Two peers resizing the same table at once can interleave dashes inside the
 * one markdown line (Yjs merges the Y.Text, not the row). The sum is then no longer 100 and the
 * table falls back to auto; re-dragging repairs it. Same contract as a malformed image `=NN%`.
 *
 * Parity: `backend/domain/table-geometry.ts` holds an identical copy (the web app cannot import
 * backend source), mechanically pinned by `shared/table-geometry.fixture.json`, which BOTH sides
 * assert against. Keep the two in sync — the same arrangement as image-geometry.ts.
 */

/** Narrowest column, in percent of the table width. Also what tells a sized row from `---`. */
export const COL_MIN_PCT = 5
/** What the column shares of an explicit row add up to. */
export const COL_TOTAL_PCT = 100

/** The auto delimiter cell — what tiptap-markdown has always written. */
const AUTO_CELL = '---'
/** One delimiter cell: optional colons around a run of dashes. */
const DELIMITER_CELL = /^:?(-+):?$/

/**
 * True when `percents` is an explicit width set as the grammar defines it: at least two whole
 * numbers, each at least COL_MIN_PCT, summing to exactly COL_TOTAL_PCT.
 */
export function isExplicitWidths(percents: readonly number[] | null | undefined): percents is number[] {
  if (!percents || percents.length < 2) return false
  let sum = 0
  for (const p of percents) {
    if (!Number.isInteger(p) || p < COL_MIN_PCT) return false
    sum += p
  }
  return sum === COL_TOTAL_PCT
}

/**
 * Read a delimiter row (`| ----- | :--------: |`) into its column percents, or null when the row
 * is auto. Leading blockquote markers and indentation are skipped, so a row read straight from the
 * source line of a nested table works too. Never throws: anything unreadable is auto.
 */
export function parseDelimiterRow(line: string | null | undefined): number[] | null {
  const body = (line ?? '').replace(/^[\s>]*/, '').trim().replace(/^\|/, '').replace(/\|$/, '')
  if (body.trim() === '') return null
  const counts: number[] = []
  for (const cell of body.split('|')) {
    const match = DELIMITER_CELL.exec(cell.trim())
    if (!match) return null
    counts.push(match[1]!.length)
  }
  return isExplicitWidths(counts) ? counts : null
}

/**
 * Turn any set of positive column sizes (pixels, raw shares, …) into a valid explicit width set:
 * whole percent, every column at least COL_MIN_PCT, summing to COL_TOTAL_PCT, rounded with the
 * largest-remainder method so the rounding error never piles onto one column. A set that is
 * already valid comes back unchanged. Null when no valid set exists — fewer than two columns, too
 * many columns for the minimum, or sizes that are not finite non-negative numbers with a positive
 * sum.
 */
export function normaliseWidths(raw: readonly number[]): number[] | null {
  const n = raw.length
  if (n < 2 || n * COL_MIN_PCT > COL_TOTAL_PCT) return null
  if (raw.some((w) => !Number.isFinite(w) || w < 0)) return null
  if (raw.reduce((a, b) => a + b, 0) <= 0) return null

  // Clamp: a column whose share falls under the minimum is pinned there, and the rest of the
  // budget is re-shared among the others in their original proportions — repeated, because the
  // re-share can only push the remaining columns UP, never under the minimum a second time.
  const pinned = new Array<boolean>(n).fill(false)
  let shares: number[] = []
  for (;;) {
    const budget = COL_TOTAL_PCT - COL_MIN_PCT * pinned.filter(Boolean).length
    const freeSum = raw.reduce((a, w, i) => (pinned[i] ? a : a + w), 0)
    shares = raw.map((w, i) => (pinned[i] ? COL_MIN_PCT : freeSum > 0 ? (w * budget) / freeSum : 0))
    let moved = false
    shares.forEach((s, i) => {
      if (!pinned[i] && s < COL_MIN_PCT) {
        pinned[i] = true
        moved = true
      }
    })
    if (!moved) break
  }

  // Largest remainder: floor everything, then hand the missing units to the biggest fractions
  // (ties to the leftmost column, so the result is deterministic on both sides).
  const floors = shares.map((s) => Math.floor(s))
  let missing = COL_TOTAL_PCT - floors.reduce((a, b) => a + b, 0)
  const order = shares
    .map((s, i) => ({ i, frac: s - Math.floor(s) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i)
  for (const { i } of order) {
    if (missing <= 0) break
    floors[i] = floors[i]! + 1
    missing--
  }
  return isExplicitWidths(floors) ? floors : null
}

/**
 * Write the delimiter row for a table of `columns` columns. With a width set of the right length
 * it is normalised (a no-op for a valid set) and written as dash counts; otherwise — and for
 * `null` — it is the auto row `| --- | --- |`, byte-identical to what tiptap-markdown writes.
 */
export function formatDelimiterRow(columns: number, percents: readonly number[] | null | undefined): string {
  const widths = percents && percents.length === columns ? normaliseWidths(percents) : null
  const cells = widths
    ? widths.map((w) => '-'.repeat(w))
    : Array.from({ length: columns }, () => AUTO_CELL)
  return `| ${cells.join(' | ')} |`
}
